import { createStore } from "zustand/vanilla";
import { preloadTranslationData } from "./translation";
import { preloadInfoData } from "./info";
import { configRepo } from "./config";

type TPreloadKey = string;

export interface IPreloadRepo {
  items: Record<TPreloadKey, { part: number; progress: number }>;
  loadingItems: TPreloadKey[];
  loadedItems: TPreloadKey[];
  progress: number; // from 0 to 100
  isActive: boolean;
  setProgress: (key: TPreloadKey, localPercent: number) => void;
  setLoading: (key: TPreloadKey) => void;
  finish: (key: TPreloadKey) => void;
  loadConfigItems: () => void;
}

export const preloadRepo = createStore<IPreloadRepo>((set, get) => ({
  progress: 0,
  isActive: true,
  items: {
    assets: {
      part: 10,
      progress: 0,
    },
    info: {
      part: 10,
      progress: 0,
    },
    translations: {
      part: 10,
      progress: 0,
    },
    gameFiles: {
      part: 70,
      progress: 0,
    },
  },
  loadingItems: [],
  loadedItems: [],
  setProgress: (key, localPercent) => {
    set(({ items }) => {
      let newProgress = 0;

      const newItems = Object.entries(items).reduce<IPreloadRepo["items"]>(
        (acc, [k, item]) => {
          if (key === k) {
            item.progress = (localPercent * item.part) / 100;
          }
          newProgress += item.progress;

          acc[k] = item;
          return acc;
        },
        {}
      );

      return {
        items: newItems,
        progress: newProgress,
      };
    });
  },

  setLoading: (key) => {
    set(({ loadingItems }) => ({
      loadingItems: loadingItems.concat(key),
    }));
  },

  finish: (key) => {
    set(({ loadedItems }) => ({
      loadedItems: loadedItems.concat(key),
    }));
  },

  loadConfigItems: async () => {
    const { loadingItems, setLoading } = get();

    if (!loadingItems.includes("translations")) {
      setLoading("translations");
      await preloadTranslationData();
    }

    if (!loadingItems.includes("info")) {
      setLoading("info");
      await preloadInfoData();
    }

    if (!loadingItems.includes("assets")) {
      setLoading("assets");
      configRepo.getState().loadAssets();
    }

  },
}));

preloadRepo.subscribe(({ loadedItems, items, progress }, prev) => {
  if (
    progress === prev.progress &&
    loadedItems.length === prev.loadedItems.length
  ) {
    return;
  }

  const finished = Object.keys(items).every((key) => loadedItems.includes(key));

  if (finished) {
    preloadRepo.setState({
      isActive: false,
    });
  }
});

export default preloadRepo;
